import { graphHooks } from '@tunasong/graph-lib/react'
import { AccessDenied, Check, Poll as PollIcon } from '@tunasong/icons'
import type { ManyOfManyPollResponse, PollOption } from '@tunasong/schemas'
import { isGroupedPoll } from '@tunasong/schemas'
import { Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import invariant from 'tiny-invariant'
import { getGroupOptionIndices, getVotableGroups } from '../groups.js'
import { getOptionLabel } from '../option-label.js'
import { PollAudioProvider } from '../option-audio.js'
import type { PollProps } from '../poll.js'
import { PollOptionName } from '../poll-option-name.js'
import styles from '../many-of-many/view.module.css'
import { GroupSection } from './group-section.js'

interface AnswerRowProps {
  option: PollOption
  picked: boolean
}

/** One option and whether it was picked, announced by what the row shows — the entity name for a bare link. */
const AnswerRow: FC<AnswerRowProps> = props => {
  const { option, picked } = props
  const { entity } = graphHooks.useEntity(option.entityRef)
  const label = getOptionLabel(option, entity?.name)
  return (
    <div className={styles.optionRow}>
      {picked ? (
        <Check color="var(--tuna-palette-success-main)" aria-label={`Picked: ${label}`} />
      ) : (
        <AccessDenied color="var(--tuna-content-muted)" aria-label={`Not picked: ${label}`} />
      )}
      <PollOptionName option={option} />
    </div>
  )
}

/** One member's answers to a grouped poll, read-only: what they picked in each group. */
export const GroupedPollView: FC<PollProps> = props => {
  const { poll, userId } = props

  invariant(isGroupedPoll(poll), 'Poll must be a grouped poll')

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })
  const choices = allResponses.find(response => response.userId === userId)?.choices ?? {}

  const { name = 'Untitled' } = poll

  return (
    <PollAudioProvider pollId={poll.id}>
      <div className={styles.container}>
        <Typography variant="subtitle1">
          <PollIcon /> {name}
        </Typography>
        {hasLoadedChildren
          ? getVotableGroups(poll).map(group => {
              const indices = getGroupOptionIndices(poll, group.id)
              return (
                <GroupSection key={group.id} name={group.name} order={indices}>
                  {indices.map(idx => (
                    <AnswerRow key={idx} option={poll.pollOptions[idx]} picked={Boolean(choices[idx])} />
                  ))}
                </GroupSection>
              )
            })
          : null}
      </div>
    </PollAudioProvider>
  )
}

export default GroupedPollView
